import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
} from "@mui/material";
import { ArrowBack, Edit, LockReset, Lock } from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import ProfileView from "../components/settings/ProfileView";
import apiEndpoints from "../apiConfig";
import LogoutConfirmDialog from "../components/ui/LogoutConfirmDialog";

const MOCK_CURRENT_PASSWORD = "123456";

function SettingsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current: "",
    newPassword: "",
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);

  useEffect(() => {
    if (user?.user_id) {
      fetch(
        `${apiEndpoints.register}?action=fetch&user_id=${user.user_id}&current_user_id=${user.id}`,
      )
        .then((res) => res.json())
        .then((data) => {
          if (data) {
            // prepend apiEndpoints.image to all image fields
            const imageFields = [
              "profile_photo",
              "profile_photo1",
              "profile_photo2",
              "profile_photo3",
              "horoscope",
              "ID_proof",
            ];
            imageFields.forEach((field) => {
              if (data[field]) {
                data[field] = apiEndpoints.image + data[field];
              }
            });
          }
          setProfileData(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error(err);
          setLoading(false);
        });
    }
  }, [user]);

  const resetPasswordForm = () => {
    setPasswordForm({ current: "", newPassword: "" });
    setPasswordErrors({});
  };

  const handleChangePassword = () => {
    resetPasswordForm();
    setPasswordDialogOpen(true);
  };

  const handlePasswordDialogClose = () => {
    if (isSubmittingPassword) return;
    setPasswordDialogOpen(false);
    resetPasswordForm();
  };

  const handlePasswordInputChange = (field) => (event) => {
    setPasswordForm((prev) => ({ ...prev, [field]: event.target.value }));
    setPasswordErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handlePasswordSubmit = () => {
    const errors = {};

    if (!passwordForm.current.trim()) {
      errors.current = "Current password is required.";
    } else if (passwordForm.current !== MOCK_CURRENT_PASSWORD) {
      errors.current = "Incorrect current password.";
    }

    if (!passwordForm.newPassword.trim()) {
      errors.newPassword = "New password is required.";
    } else if (passwordForm.newPassword.length < 6) {
      errors.newPassword = "Password must be at least 6 characters.";
    }

    if (Object.keys(errors).length) {
      setPasswordErrors(errors);
      return;
    }

    setIsSubmittingPassword(true);
    setTimeout(() => {
      setIsSubmittingPassword(false);
      showToast("Password changed successfully", "success");
      handlePasswordDialogClose();
    }, 600);
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleConfirmLogout = () => {
    setLogoutDialogOpen(false);
    logout();
    showToast("Logged out successfully", "success");
    navigate("/");
  };

  if (!user) return null;

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "background.default" }}>
      <Container maxWidth="100%">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1,
            px: 2,
          }}
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate("/home")}
            color="inherit"
            sx={{ color: "black", minWidth: "auto" }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => navigate("/edit-profile")}
            color="secondary"
          >
            Edit Profile
          </Button>
        </Box>
      </Container>

      <Container maxWidth="100%" sx={{ py: 1 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
            Profile Overview
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and manage your complete matrimony profile
          </Typography>
        </Box>

        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <ProfileView profile={profileData} />
        )}

        {/* Account Actions */}
        <Box
          sx={{ mt: 4, mb: 4, p: 2, border: "1px solid #eee", borderRadius: 2 }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Account Settings
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button
              variant="outlined"
              color="primary"
              onClick={handleChangePassword}
              sx={{ mb: { xs: 1, sm: 0 } }}
            >
              Change Password
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={handleLogoutClick}
              sx={{ mb: { xs: 1, sm: 0 } }}
            >
              Logout
            </Button>
          </Stack>
        </Box>
      </Container>
      <LogoutConfirmDialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={handleConfirmLogout}
      />
      <Dialog
        open={passwordDialogOpen}
        onClose={handlePasswordDialogClose}
        PaperProps={{
          sx: {
            borderRadius: 4,
            px: 2,
            pb: 2,
            width: { xs: "90%", sm: 450 },
            maxWidth: 450,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 700,
          }}
        >
          <LockReset color="primary" />
          Change Password
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 0 }}>
          <TextField
            label="Current Password"
            type="password"
            fullWidth
            value={passwordForm.current}
            onChange={handlePasswordInputChange("current")}
            error={Boolean(passwordErrors.current)}
            helperText={passwordErrors.current}
            margin="dense"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock fontSize="small" />
                </InputAdornment>
              ),
              sx: { borderRadius: 2 },
            }}
            autoComplete="current-password"
          />
          <TextField
            label="New Password"
            type="password"
            fullWidth
            value={passwordForm.newPassword}
            onChange={handlePasswordInputChange("newPassword")}
            error={Boolean(passwordErrors.newPassword)}
            helperText={passwordErrors.newPassword}
            margin="dense"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock fontSize="small" />
                </InputAdornment>
              ),
              sx: { borderRadius: 2 },
            }}
            autoComplete="new-password"
          />
        </DialogContent>
        <DialogActions sx={{ px: 0, justifyContent: "space-between" }}>
          <Button
            onClick={handlePasswordDialogClose}
            variant="outlined"
            color="inherit"
            disabled={isSubmittingPassword}
            sx={{ borderRadius: 20, px: 3, height: 40 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePasswordSubmit}
            variant="contained"
            color="warning"
            disabled={isSubmittingPassword}
            sx={{
              borderRadius: 20,
              px: 3,
              textTransform: "none",
              height: 40,
            }}
          >
            {isSubmittingPassword ? "Saving…" : "Update Password"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SettingsPage;
